/**
 * Persist patches until the server has confirmed them.
 *
 * A patch only ever reaches the server if the worker is alive to send it, and the browser can kill
 * a worker without telling anyone. Every patch therefore goes into a `localStorage` backlog before
 * it is handed over, and only leaves it once `patch_published` came back. Whatever is still in the
 * backlog is handed over again, so a patch survives a worker that died under it.
 */
import {
    AccountUID,
    BoardUID,
    from_buffer,
    PatchBacklogEntry,
    PatchBacklogMetadata,
    PatchUID,
    to_uint8array,
} from "@cling/lib.shared.model"
import type {EventBus} from "@cling/lib.web.primitives"
import {
    local_storage_work_queue,
    LocalStorageWorkQueue,
} from "@cling/lib.web.primitives/local_storage_work_queue"
import type {PatchPublished, PublishPatch} from "@cling/lib.web.worker/worker_interface"
import {report_error} from "@cling/lib.shared.debug"
import {nop} from "@cling/lib.shared.utils"
import {RateLimiter} from "@cling/lib.shared.utils/rate_limiter"

const FIRST_STEP_DELAY = 5_000
const STEP_INTERVAL = 60_000
/**
 * A patch is only handed over again once it is older than this. It is deliberately longer than
 * `STEP_INTERVAL` so that the backlog can never re-send a patch the normal path is still working
 * on, which makes it a cross-session mechanism in practice.
 */
const MIN_AGE = STEP_INTERVAL + 30_000
const BATCH_SIZE = 25
/**
 * `localStorage` is about 5 MB for the whole origin, and filling it up would break every other
 * writer in the app, auth included. So neither a single patch nor the backlog as a whole may grow
 * past these. Losing the protection for what does not fit is better than that.
 */
const MAX_ENTRY_BYTES = 256 * 1024
const MAX_BACKLOG_BYTES = 2 * 1024 * 1024

export type PublishArgs = {
    patch_uid: PatchUID
    board_uids: Array<BoardUID>
    patch_pb: Uint8Array
    transformed_patches_pb: Array<Uint8Array>
}

export function init({
    account_uid,
    publish_patch_bus,
    publish_patch,
    disabled = false,
    first_step_delay = FIRST_STEP_DELAY,
    step_interval = STEP_INTERVAL,
    storage,
}: {
    account_uid: AccountUID
    publish_patch_bus: EventBus<PublishPatch | PatchPublished>
    publish_patch: (args: PublishArgs) => void
    /** Frontend-only mode has no server to publish to, so nothing could ever leave the backlog. */
    disabled?: boolean
    first_step_delay?: number
    step_interval?: number
    storage?: Storage
}) {
    const passthrough = {publish_patch, size: () => 0, start: nop, stop: nop}
    if (disabled) {
        return passthrough
    }
    const report_limiter = new RateLimiter({max_calls: 1, per: [5, "minutes"]})
    // Handing the same patch to the same worker twice only grows that worker's own queue, so we do
    // it at most once per page session. A reload makes every patch eligible again, and a reload is
    // also the only thing that can replace a worker that died.
    const handed_over = new Set<PatchUID>()
    let queue: LocalStorageWorkQueue<PatchBacklogMetadata>
    let timeout: any
    let stopped = false
    // Nothing this page enqueued can be in flight yet, so whatever is already in the backlog
    // is left over from an earlier session and should go out at once rather than age first.
    let first_step = true
    try {
        queue = local_storage_work_queue<PatchBacklogMetadata>({
            name: `${account_uid}:patch_backlog`,
            metadata_type: PatchBacklogMetadata,
            min_age: MIN_AGE,
            storage,
        })
    } catch (error) {
        report_error("[patch_backlog] Patches are not persisted on this device", error)
        return passthrough
    }

    function report_throttled(message: string, error_or_extra: Error | Record<string, any>) {
        report_limiter.run((within_limit) => {
            if (within_limit) {
                report_error(message, error_or_extra)
            }
        })
    }

    function hand_over(args: PublishArgs) {
        handed_over.add(args.patch_uid)
        publish_patch(args)
    }

    async function step() {
        try {
            await queue.step(
                async (batch) => {
                    // Only `patch_published` may drop an entry that could still be sent. Handing
                    // a patch to the worker says nothing about whether the server ever got it.
                    const remove_keys: Array<string> = []
                    for (const {key, value, metadata} of batch) {
                        if (handed_over.has(metadata.patch_uid)) {
                            continue
                        }
                        let entry: PatchBacklogEntry
                        try {
                            entry = from_buffer(PatchBacklogEntry, value)
                        } catch (error) {
                            // The batch is always the head of the queue, so an entry that cannot
                            // be read would block everything behind it on every page load.
                            report_throttled("[patch_backlog] Dropping an unreadable patch", error)
                            remove_keys.push(key)
                            continue
                        }
                        hand_over({
                            patch_uid: metadata.patch_uid,
                            board_uids: metadata.board_uids,
                            patch_pb: entry.patch_pb,
                            transformed_patches_pb: entry.transformed_patches_pb,
                        })
                    }
                    return {remove_keys}
                },
                {batch_size: BATCH_SIZE, min_age: first_step ? 0 : MIN_AGE},
            )
        } catch (error) {
            report_throttled("[patch_backlog] Failed to work off the backlog", error)
        } finally {
            first_step = false
            if (!stopped) {
                timeout = setTimeout(step, step_interval)
            }
        }
    }

    publish_patch_bus.addEventListener((event) => {
        const msg = event.data
        if (msg.type !== "patch_published") {
            return
        }
        handed_over.delete(msg.patch_uid)
        try {
            // A patch the worker gave up on is dropped, too. The user was already told about it and
            // re-sending it would fail just the same.
            queue.remove((entry) => entry.metadata.patch_uid === msg.patch_uid)
        } catch (error) {
            report_throttled("[patch_backlog] Failed to drop a published patch", error)
        }
    })

    return {
        /**
         * Persist the patch and hand it to the worker. The patch is durable before it is handed
         * over, so a worker that dies right afterwards cannot take it with it.
         */
        publish_patch: (args: PublishArgs) => {
            const value = to_uint8array(
                new PatchBacklogEntry({
                    patch_pb: args.patch_pb,
                    transformed_patches_pb: args.transformed_patches_pb,
                }),
            )
            const metadata = new PatchBacklogMetadata({
                patch_uid: args.patch_uid,
                board_uids: args.board_uids,
            })
            const backlog_bytes = queue.bytes()
            if (value.length > MAX_ENTRY_BYTES || backlog_bytes > MAX_BACKLOG_BYTES) {
                report_throttled(
                    "[patch_backlog] Not persisting a patch, it or the backlog is too big",
                    {
                        patch_uid: args.patch_uid,
                        bytes: value.length,
                        backlog_bytes,
                    },
                )
            } else {
                queue.enqueue(value, metadata).catch((error) =>
                    // Publishing still has to happen, the patch is just not protected.
                    report_throttled("[patch_backlog] Failed to persist a patch", error),
                )
            }
            hand_over(args)
        },
        /** How many patches the server has not confirmed yet. */
        size: () => queue.size(),
        start: () => {
            stopped = false
            first_step = true
            clearTimeout(timeout)
            timeout = setTimeout(step, first_step_delay)
        },
        stop: () => {
            stopped = true
            clearTimeout(timeout)
        },
    }
}
